import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useUserLocale } from '@shared/utils/composables/locales'
import getLocalizedLabel from '@shared/utils/helpers/localized-label'

import { Participant } from '@protos/chat/participant_pb'
import { ChatWidgetMessageType } from '@protos/chat_widget_message_type_pb'
import { UserType } from '@protos/user_type_pb'

import { useConversationsStore } from '@pc/store/conversations'
import { useSessionStore } from '@pc/store/session'

export const useAwayMessageConfigurator = (userType?: UserType) => {
  const { userLocale } = useUserLocale()
  const sessionStore = useSessionStore()
  const conversationsStore = useConversationsStore()

  const { currentConversation } = storeToRefs(conversationsStore)

  const participant = computed(() => {
    const userTypes = [UserType.VISITOR, UserType.PATIENT]

    return currentConversation.value?.participants?.find((item: Participant) =>
      userTypes.includes(item.type)
    )
  })

  const participantType = computed(
    () => userType ?? participant.value?.type ?? UserType.USER
  )

  const currentWidget = computed(() => sessionStore.chatProps?.chatWidget)

  const awayMessageForAnonymus = computed(() =>
    currentWidget.value?.messages?.find(
      ({ messageType }) => messageType === ChatWidgetMessageType.ANONYMOUS
    )
  )

  const awayMessageForKnown = computed(() =>
    currentWidget.value?.messages?.find(
      ({ messageType }) => messageType === ChatWidgetMessageType.KNOWN
    )
  )

  const message = computed(() =>
    participantType.value === UserType.PATIENT
      ? getLocalizedLabel(
          awayMessageForKnown.value?.localizedTexts,
          userLocale.value
        ).value
      : getLocalizedLabel(
          awayMessageForAnonymus.value?.localizedTexts,
          userLocale.value
        ).value
  )

  const isDisplay = computed(() =>
    //TO DO check is it correct isDisplay ?? true
    participantType.value === UserType.PATIENT
      ? (awayMessageForKnown.value?.isDisplay ?? true)
      : (awayMessageForAnonymus.value?.isDisplay ?? true)
  )

  const quickFormId = computed(() =>
    participantType.value === UserType.PATIENT
      ? awayMessageForKnown.value?.quickFormId
      : awayMessageForAnonymus.value?.quickFormId
  )

  const messageId = computed(() =>
    participantType.value === UserType.PATIENT
      ? awayMessageForKnown.value?.id
      : awayMessageForAnonymus.value?.id
  )

  const agent = computed(() => {
    const assignee = currentConversation.value?.assignee
    if (assignee && assignee.type === UserType.USER) {
      return assignee
    }
    return null
  })

  const isAwayMessageShown = computed(() => {
    if (isDisplay.value) {
      return participant.value && agent.value && !agent.value.isOnline
    }
    return isDisplay.value
  })

  return {
    message,
    isDisplay,
    quickFormId,
    messageId,
    isAwayMessageShown,
  }
}
